(function () {
  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  const VIEW_TRANSITION_CLONE_ATTRIBUTE = 'data-view-transition-clone';

  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else if (viewTransitionRenderBlocker?.getAttribute('href') === '#ProductTransitionReady') {
    waitForExpectedElement('ProductTransitionReady', 6000);
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    // However, on slower networks, we extend this to give the DOM time to parse so the transition animation doesn't fail.
    let targetFcp = 1800;

    /* eslint-disable-next-line compat/compat */
    if (navigator.connection && ['slow-2g', '2g', '3g'].includes(navigator.connection.effectiveType)) {
      targetFcp = 4000;
    }

    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, targetFcp - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Ignore modifier / non-primary clicks (open-in-new-tab, etc.) — those
      // don't navigate the current page, so marking a transition would leave
      // stale state that mis-applies on the next real navigation.
      const mouseEvent = /** @type {MouseEvent} */ (event);
      if (mouseEvent.button !== 0 || mouseEvent.metaKey || mouseEvent.ctrlKey || mouseEvent.shiftKey || mouseEvent.altKey) {
        return;
      }

      const interactiveElement = target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;

      sessionStorage.removeItem('pending-transition-type');

      const cartTransitionSource = getCartTransitionSource(target);
      if (cartTransitionSource) {
        markViewTransition(cartTransitionSource, 'cart-product-image-transition');
        return;
      }

      const card = target.closest(
        'product-card-link[data-product-transition="true"], product-card[data-product-transition="true"]'
      );
      const cardGallery = card?.querySelector('[data-view-transition-to-main-product]');

      if (cardGallery) {
        prepareProductTransitionSource(card, cardGallery);
        markViewTransition(cardGallery, 'product-image-transition');
        return;
      }

      // Fallback for other items with data-view-transition-to-main-product (e.g., resource cards, product tabs card)
      const otherItemGallery = target.closest('[data-view-transition-to-main-product]');
      if (otherItemGallery) {
        markViewTransition(otherItemGallery, 'product-image-transition');
        return;
      }

      const link = target.closest('a[href]');

      // Broad fallback: any /products/ link that slipped through the specific checks above
      // still gets the product-image-transition so the page never falls back to a bare fade.
      if (link instanceof HTMLAnchorElement) {
        try {
          const dest = new URL(link.href);
          if (dest.origin === window.location.origin && dest.pathname.includes('/products/')) {
            const imgEl = link.querySelector('img') ?? link;
            markViewTransition(imgEl, 'product-image-transition');
            return;
          }
        } catch {}
      }

      const grid = document.querySelector('.product-grid');

      if (shouldUseCollectionGridTransition(link) && grid) {
        markViewTransition(grid, 'collection-grid');
      }
    },
    { capture: true }
  );

  window.addEventListener('pageswap', async (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint)
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          viewTransition.skipTransition();
        },
        { once: true }
      );
    });

    // Clean in case you landed on the PDP first. The outgoing product page can
    // already have product/cart target markers on its gallery, which would
    // compete with the clicked source for the same view-transition-name.
    document
      .querySelectorAll(
        '[data-view-transition-type]:not([data-view-transition-triggered]), [data-cart-view-transition-type]:not([data-view-transition-triggered])'
      )
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
        element.removeAttribute('data-cart-view-transition-type');
      });

    const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
    const transitionType =
      transitionTriggered?.getAttribute('data-view-transition-type') ?? sessionStorage.getItem('pending-transition-type');

    if (transitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(transitionType);
      sessionStorage.setItem('custom-transition-type', transitionType);
      sessionStorage.removeItem('pending-transition-type');
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
      sessionStorage.removeItem('custom-transition-type');
    }
  });

  window.addEventListener('pagereveal', async (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    const customTransitionType = sessionStorage.getItem('custom-transition-type');

    if (customTransitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(customTransitionType);

      await viewTransition.finished;

      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');

      idleCallback(() => {
        sessionStorage.removeItem('custom-transition-type');
        sessionStorage.removeItem('pending-transition-type');
        cleanupViewTransitionSources();
      });
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
      sessionStorage.removeItem('pending-transition-type');
    }
  });

  // Back/forward from the bfcache replays the DOM exactly as it was left, which includes
  // the fixed-position clone markViewTransition() appends when the clicked source sits
  // inside a <dialog> (the search modal). Nothing else removes it on the way back:
  // pagereveal does not fire for a restored document, and even when it does, the outgoing
  // pageswap has already cleared `custom-transition-type`, so it takes the else branch
  // above, which skips cleanup. The clone is position:fixed at max z-index, so the clicked
  // product image stays pinned over the page — visible as soon as the modal is closed.
  //
  // Guarded on `persisted` deliberately: a non-restored pageshow fires after load, which
  // can land while pagereveal is still awaiting viewTransition.finished, and cleaning up
  // then would strip view-transition-name mid-animation.
  window.addEventListener('pageshow', (event) => {
    if (!(/** @type {PageTransitionEvent} */ (event).persisted)) return;

    cleanupViewTransitionSources();
  });

  /**
   * @param {ViewTransition | null} viewTransition
   * @returns {viewTransition is null}
   */
  function shouldSkipViewTransition(viewTransition) {
    return typeof ViewTransition === 'undefined' || !(viewTransition instanceof ViewTransition) || isLowPowerDevice();
  }

  /**
   * @param {Element} element
   * @param {string} transitionType
   */
  function markViewTransition(element, transitionType) {
    cleanupViewTransitionSources(element);
    sessionStorage.setItem('pending-transition-type', transitionType);

    // Workaround for <dialog> top layer clearing on navigation
    if (element.closest('dialog')) {
      const rect = element.getBoundingClientRect();
      const clone = /** @type {HTMLElement} */ (element.cloneNode(true));

      if (!(clone instanceof HTMLElement) || rect.width <= 0 || rect.height <= 0) return;

      // Remove any IDs to avoid conflicts, and ensure it visually matches
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((child) => child.removeAttribute('id'));
      clone.style.position = 'fixed';
      clone.style.top = `${rect.top}px`;
      clone.style.left = `${rect.left}px`;
      clone.style.width = `${rect.width}px`;
      clone.style.height = `${rect.height}px`;
      clone.style.margin = '0';
      clone.style.zIndex = '2147483647'; // Max z-index to stay on top
      clone.style.pointerEvents = 'none';
      clone.style.viewTransitionName = getViewTransitionName(transitionType);

      clone.setAttribute(VIEW_TRANSITION_CLONE_ATTRIBUTE, 'true');
      clone.setAttribute('data-view-transition-type', transitionType);
      clone.setAttribute('data-view-transition-triggered', 'true');
      clone.setAttribute('aria-hidden', 'true');

      document.body.appendChild(clone);
    } else {
      element.setAttribute('data-view-transition-type', transitionType);
      element.setAttribute('data-view-transition-triggered', 'true');
      if (element instanceof HTMLElement) {
        element.style.viewTransitionName = getViewTransitionName(transitionType);
      }
    }
  }

  /**
   * @param {string} transitionType
   * @returns {string}
   */
  function getViewTransitionName(transitionType) {
    if (transitionType === 'product-image-transition' || transitionType === 'cart-product-image-transition') {
      return 'product-image-transition';
    }

    return transitionType;
  }

  /**
   * @param {Element} [exceptElement]
   */
  function cleanupViewTransitionSources(exceptElement) {
    document
      .querySelectorAll(`[data-view-transition-triggered], [${VIEW_TRANSITION_CLONE_ATTRIBUTE}]`)
      .forEach((element) => {
        if (element === exceptElement) return;

        if (element.hasAttribute(VIEW_TRANSITION_CLONE_ATTRIBUTE)) {
          element.remove();
          return;
        }

        element.removeAttribute('data-view-transition-triggered');
        element.removeAttribute('data-view-transition-type');
        if (element instanceof HTMLElement) {
          element.style.removeProperty('view-transition-name');
        }
      });
  }

  /**
   * @param {Element} target
   * @returns {Element | null}
   */
  function getCartTransitionSource(target) {
    const directSource = target.closest('[data-cart-view-transition-source]');
    if (directSource) return directSource;

    const cartRow = target.closest('.cart-items__table-row');
    const link = target.closest('a[href]');
    if (!cartRow || !(link instanceof HTMLAnchorElement)) return null;

    try {
      const destination = new URL(link.href);
      if (destination.origin !== window.location.origin || !destination.pathname.includes('/products/')) return null;
    } catch {
      return null;
    }

    return cartRow.querySelector('[data-cart-view-transition-source]');
  }

  /**
   * Ensures the outgoing image has a loaded source for the browser snapshot.
   * Hover secondary images are left visible; the transition source remains the
   * whole gallery/media wrapper.
   *
   * @param {Element | null | undefined} card
   * @param {Element} cardGallery
   */
  function prepareProductTransitionSource(card, cardGallery) {
    const featuredMediaUrl = card?.getAttribute('data-featured-media-url');
    if (!featuredMediaUrl) return;

    const transitionImage = getTransitionImage(card) ?? getTransitionImage(cardGallery);
    if (!(transitionImage instanceof HTMLImageElement)) return;
    if (imageMatchesUrl(transitionImage, featuredMediaUrl)) return;

    transitionImage.srcset = featuredMediaUrl;
    transitionImage.src = featuredMediaUrl;
  }

  /**
   * @param {HTMLImageElement} image
   * @param {string} targetUrl
   * @returns {boolean}
   */
  function imageMatchesUrl(image, targetUrl) {
    const currentSrc = image.currentSrc || image.src;
    if (!currentSrc) return false;

    try {
      const currentImageUrl = new URL(currentSrc, window.location.href);
      const currentImagePath = currentImageUrl.host + currentImageUrl.pathname;
      return targetUrl.includes(currentImagePath);
    } catch {
      return false;
    }
  }

  /**
   * @param {Element | null | undefined} scope
   * @returns {HTMLImageElement | undefined}
   */
  function getTransitionImage(scope) {
    if (!scope) return;

    const images = Array.from(scope.querySelectorAll('img[ref*="imagesToTransition"]')).filter(
      (image) => image instanceof HTMLImageElement
    );

    return (
      images.find((image) => image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false') ?? images[0]
    );
  }

  /**
   * @param {string} id
   * @param {number} timeout
   */
  function waitForExpectedElement(id, timeout) {
    if (!viewTransitionRenderBlocker) return;

    if (document.getElementById(id)) {
      viewTransitionRenderBlocker.remove();
      return;
    }

    const observer = new MutationObserver(() => {
      if (!document.getElementById(id)) return;

      observer.disconnect();
      viewTransitionRenderBlocker.remove();
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      viewTransitionRenderBlocker.remove();
    }, timeout);
  }

  /**
   * @param {Element | null} link
   */
  function shouldUseCollectionGridTransition(link) {
    if (!(link instanceof HTMLAnchorElement)) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;

    let targetUrl;
    try {
      targetUrl = new URL(link.href, window.location.href);
    } catch {
      return false;
    }
    if (targetUrl.origin !== window.location.origin) return false;

    const isCollectionSource =
      window.location.pathname.includes('/collections/') && !window.location.pathname.includes('/products/');
    const isCollectionTarget =
      targetUrl.pathname.includes('/collections/') && !targetUrl.pathname.includes('/products/');

    return isCollectionSource && isCollectionTarget;
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }
})();
